import React, { useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useContactModal } from '../App';
import { FaArrowRight, FaStar, FaHeart, FaBriefcase, FaHeartbeat, FaOm, FaPhone } from 'react-icons/fa';
import { GiStarShuriken } from 'react-icons/gi';
import horoscope2026 from '../data/horoscope2026';
import './Horoscope.css';

const Horoscope = () => {
  const { openContactModal } = useContactModal();
  const location = useLocation();
  const sectionRefs = useRef({});

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && sectionRefs.current[hash]) {
      setTimeout(() => {
        sectionRefs.current[hash].scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [location.hash]);

  return (
    <div>
      {/* Hero Banner */}
      <section className="pt-[140px] pb-20 max-md:pt-[100px] max-md:pb-12 bg-gradient-hero relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(30)].map((_, i) => (
            <div key={i} className="absolute bg-white rounded-full animate-twinkle" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
            }} />
          ))}
        </div>
        <div className="container-custom relative z-[2] text-center">
          <div className="inline-flex items-center gap-2 py-2.5 px-7 bg-accent-gold/10 border border-accent-gold/25 rounded-full text-accent-gold text-[0.85rem] font-medium mb-6 tracking-wider backdrop-blur-[10px]">
            <GiStarShuriken /> Yearly Horoscope 2026
          </div>
          <h1 className="font-heading text-[3.5rem] max-lg:text-[2.8rem] max-md:text-[2rem] font-extrabold bg-gradient-to-r from-accent-gold via-accent-yellow to-accent-saffron bg-clip-text text-transparent mb-4">
            Horoscope 2026
          </h1>
          <p className="text-white/70 text-lg max-w-[600px] mx-auto mb-8">
            Discover what the stars have in store for you this year. Explore detailed predictions for all 12 zodiac signs.
          </p>
          {/* Quick navigation */}
          <div className="flex flex-wrap justify-center gap-3 max-w-[700px] mx-auto">
            {horoscope2026.map((z) => (
              <a
                key={z.name}
                href={`#${z.name.toLowerCase()}`}
                className="flex items-center gap-1.5 py-2 px-4 bg-white/[0.06] border border-accent-gold/15 rounded-full text-white/80 text-sm hover:bg-white/[0.12] hover:border-accent-gold/40 hover:text-accent-gold transition-all duration-300 no-underline"
              >
                <span className="text-lg">{z.sign}</span>
                <span>{z.name}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Zodiac Sign Sections */}
      {horoscope2026.map((z, idx) => (
        <section
          key={z.name}
          id={z.name.toLowerCase()}
          ref={(el) => (sectionRefs.current[z.name.toLowerCase()] = el)}
          className={`py-16 md:py-20 ${idx % 2 === 0 ? 'bg-warm-off-white' : 'bg-white'} scroll-mt-20`}
        >
          <div className="container-custom">
            {/* Sign Header */}
            <div className="flex flex-col md:flex-row items-center gap-6 mb-10">
              <div className="horoscope-sign-icon flex-shrink-0 w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center text-5xl md:text-6xl border-2 border-accent-gold/20 bg-gradient-to-br from-primary-deep/5 to-accent-gold/5 shadow-[0_8px_30px_rgba(167,139,250,0.1)]">
                {z.sign}
              </div>
              <div className="text-center md:text-left">
                <h2 className="font-heading text-[2.2rem] max-md:text-[1.6rem] text-primary-dark mb-1">
                  {z.name} <span className="text-warm-gray font-normal text-[1.1rem]">({z.hindi})</span>
                </h2>
                <p className="text-warm-gray text-sm mb-2">{z.dates} &bull; {z.element} Sign &bull; Ruled by {z.ruler}</p>
                <div className="flex flex-wrap justify-center md:justify-start gap-3 text-xs">
                  <span className="py-1 px-3 bg-accent-gold/10 text-accent-saffron rounded-full">Lucky Numbers: {z.lucky.numbers}</span>
                  <span className="py-1 px-3 bg-accent-gold/10 text-accent-saffron rounded-full">Colors: {z.lucky.colors}</span>
                  <span className="py-1 px-3 bg-accent-gold/10 text-accent-saffron rounded-full">Gems: {z.lucky.gems}</span>
                </div>
              </div>
            </div>

            {/* Overview */}
            <div className="mb-8 p-6 md:p-8 bg-gradient-to-r from-primary-deep/[0.03] to-accent-gold/[0.05] border border-accent-gold/10 rounded-2xl">
              <div className="flex items-center gap-2 mb-3">
                <FaStar className="text-accent-gold text-lg" />
                <h3 className="font-heading text-lg text-primary-dark">Year Overview</h3>
              </div>
              <p className="text-warm-text-body leading-relaxed">{z.overview}</p>
            </div>

            {/* Detail Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="horoscope-detail-card p-6 bg-white rounded-2xl border border-warm-light-gray shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center bg-blue-50 text-blue-500">
                    <FaBriefcase />
                  </div>
                  <h4 className="font-heading text-base text-primary-dark">Career & Finance</h4>
                </div>
                <p className="text-sm text-warm-text-body leading-relaxed">{z.career}</p>
              </div>

              <div className="horoscope-detail-card p-6 bg-white rounded-2xl border border-warm-light-gray shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center bg-pink-50 text-pink-500">
                    <FaHeart />
                  </div>
                  <h4 className="font-heading text-base text-primary-dark">Love & Relationships</h4>
                </div>
                <p className="text-sm text-warm-text-body leading-relaxed">{z.love}</p>
              </div>

              <div className="horoscope-detail-card p-6 bg-white rounded-2xl border border-warm-light-gray shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center bg-green-50 text-green-500">
                    <FaHeartbeat />
                  </div>
                  <h4 className="font-heading text-base text-primary-dark">Health & Wellness</h4>
                </div>
                <p className="text-sm text-warm-text-body leading-relaxed">{z.health}</p>
              </div>

              <div className="horoscope-detail-card p-6 bg-white rounded-2xl border border-warm-light-gray shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center bg-orange-50 text-accent-saffron">
                    <FaOm />
                  </div>
                  <h4 className="font-heading text-base text-primary-dark">Vedic Remedies</h4>
                </div>
                <p className="text-sm text-warm-text-body leading-relaxed">{z.remedy}</p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <button onClick={openContactModal} className="btn-primary text-sm">
                Get Detailed {z.name} Reading <FaArrowRight />
              </button>
            </div>
          </div>
        </section>
      ))}

      {/* Bottom CTA */}
      <section className="py-[100px] max-md:py-16 bg-gradient-cosmic relative overflow-hidden text-center">
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(15)].map((_, i) => (
            <div key={i} className="absolute w-[3px] h-[3px] bg-white rounded-full animate-twinkle" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
            }} />
          ))}
        </div>
        <div className="container-custom relative z-[2]">
          <FaOm className="text-5xl text-accent-gold mb-5 animate-float mx-auto" />
          <h2 className="text-[2.2rem] max-md:text-[1.6rem] text-white mb-4 font-heading">Want a Personalized Reading?</h2>
          <p className="text-lg text-white/70 max-w-[550px] mx-auto mb-8">
            These are general predictions. For a detailed, personalized horoscope reading based on your exact birth chart, consult Acharya Arun.
          </p>
          <div className="flex gap-5 justify-center flex-wrap">
            <a href="tel:+918084768303" className="btn-primary">
              <FaPhone /> Call Now
            </a>
            <button onClick={openContactModal} className="btn-secondary !border-white !text-white hover:!bg-transparent hover:!border-accent-gold hover:!text-white">
              Book Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Source Reference */}
      <section className="py-6 bg-warm-off-white border-t border-warm-light-gray">
        <div className="container-custom text-center">
          <p className="text-xs text-warm-gray leading-relaxed">
            <span className="font-semibold">Source:</span> General horoscope predictions referenced from{' '}
            <a href="https://www.astrology.com/us/horoscope/index-yearly-2026.aspx" target="_blank" rel="noopener noreferrer" className="text-accent-saffron underline hover:text-accent-gold transition-colors">
              Astrology.com — 2026 Yearly Horoscopes
            </a>. These are general predictions and may not reflect your individual birth chart. For personalized readings, please consult Acharya Arun.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Horoscope;
